/**
 * RESTAURACIÓN DESDE UN RESPALDO JSON.
 *
 * Un respaldo que no se puede restaurar no es un respaldo: es un archivo.
 * Hasta la Fase 8 no hay sincronización, así que este es el único camino de
 * vuelta si el navegador desaloja sus datos o el teléfono se pierde.
 *
 * NO es un motor de migraciones. Valida el formato, enseña qué hay dentro,
 * pide confirmación y reemplaza. Nada más, a propósito: cuanto más listo
 * intente ser un importador, más formas tiene de dejar datos a medias.
 *
 * El JSON NUNCA lleva audio. Al restaurar, los metadatos de una grabación
 * vuelven sin su blob, y eso se dice en vez de fingir que el audio está.
 */

import { CLAVE_AJUSTES, type Ajustes } from '../domain/modelo/ajustes.js';
import type { ElementoBanco } from '../domain/modelo/banco.js';
import type { Grabacion } from '../domain/modelo/grabacion.js';
import type { SesionRegistrada } from '../domain/modelo/sesion.js';
import { metadatosNuevos } from '../domain/modelo/sincronizacion.js';
import { db, escribir } from './db.js';
import { VERSION_EXPORT, type ArchivoExportado } from './exportar.js';

export interface ResumenImport {
  exportadoEn: string | null;
  sesiones: number;
  banco: number;
  grabaciones: number;
  /** Grabaciones cuyo audio NO viene en el archivo. Siempre son todas. */
  grabacionesSinAudio: number;
  traeAjustes: boolean;
}

export type Analisis =
  | { ok: true; resumen: ResumenImport; datos: ArchivoExportado }
  | { ok: false; motivo: string };

/** Filas que se sabe leer. Todo lo demás se rechaza en vez de adivinarse. */
const TABLAS = ['sesiones', 'banco', 'grabaciones'] as const;

function esObjeto(valor: unknown): valor is Record<string, unknown> {
  return typeof valor === 'object' && valor !== null && !Array.isArray(valor);
}

/** Toda fila necesita un `id` de texto: sin identidad no se puede restaurar. */
function filasValidas(valor: unknown): valor is Record<string, unknown>[] {
  return (
    Array.isArray(valor) &&
    valor.every((fila) => esObjeto(fila) && typeof fila.id === 'string' && fila.id !== '')
  );
}

/**
 * Lee y valida el archivo SIN tocar la base.
 *
 * Devuelve el resumen para que el usuario vea qué va a restaurar ANTES de
 * decidir. Un importador que escribe primero y explica después no sirve.
 */
export function analizarImport(texto: string): Analisis {
  let crudo: unknown;
  try {
    crudo = JSON.parse(texto);
  } catch {
    return { ok: false, motivo: 'El archivo no es un JSON válido.' };
  }

  if (!esObjeto(crudo)) {
    return { ok: false, motivo: 'El archivo no tiene la forma de un respaldo.' };
  }

  if (crudo.formato !== 'english-training-hub') {
    return {
      ok: false,
      motivo:
        'Este archivo no es un respaldo de English Training Hub. No se importa nada.',
    };
  }

  const version = crudo.versionFormato;
  if (typeof version !== 'number' || !Number.isInteger(version) || version < 1) {
    return { ok: false, motivo: 'El respaldo no declara una versión de formato válida.' };
  }
  if (version > VERSION_EXPORT) {
    return {
      ok: false,
      motivo: `El respaldo es de una versión más nueva (${version}) que esta aplicación (${VERSION_EXPORT}). Actualizá la aplicación antes de restaurarlo.`,
    };
  }

  for (const tabla of TABLAS) {
    if (!filasValidas(crudo[tabla])) {
      return {
        ok: false,
        motivo: `El respaldo tiene la sección «${tabla}» dañada o incompleta. No se importa nada.`,
      };
    }
  }

  const grabaciones = crudo.grabaciones as Record<string, unknown>[];

  return {
    ok: true,
    datos: crudo as unknown as ArchivoExportado,
    resumen: {
      exportadoEn: typeof crudo.exportadoEn === 'string' ? crudo.exportadoEn : null,
      sesiones: (crudo.sesiones as unknown[]).length,
      banco: (crudo.banco as unknown[]).length,
      grabaciones: grabaciones.length,
      // El export nunca lleva blobs, así que ninguna grabación trae audio.
      grabacionesSinAudio: grabaciones.length,
      traeAjustes: esObjeto(crudo.ajustes),
    },
  };
}

export interface ResultadoRestauracion {
  sesiones: number;
  banco: number;
  grabaciones: number;
}

/**
 * REEMPLAZA los datos locales por los del respaldo.
 *
 * Es un reemplazo, no una fusión. Fusionar exigiría decidir quién gana en
 * cada campo sin tener `updatedAt` de servidor, que es justo el problema que
 * la Fase 8 resuelve con detección de conflictos. Mezclar a ciegas produciría
 * un estado que no es ni el respaldo ni lo que había.
 *
 * Todo ocurre en UNA transacción: si algo falla, no queda nada a medias.
 */
export async function restaurar(datos: ArchivoExportado): Promise<ResultadoRestauracion> {
  const ahora = new Date();

  // Las filas exportadas vienen SIN metadatos de sincronización: se limpian al
  // exportar. Al volver se les ponen de nuevo, marcadas como pendientes de
  // subir, que es exactamente lo que son.
  const conMetadatos = <T,>(filas: unknown[]): T[] =>
    filas.map((fila) => ({ ...(fila as object), ...metadatosNuevos(ahora) }) as T);

  const sesiones = conMetadatos<SesionRegistrada>(datos.sesiones);
  const banco = conMetadatos<ElementoBanco>(datos.banco);
  const grabaciones = conMetadatos<Grabacion>(datos.grabaciones);

  await escribir('restaurar el respaldo', () =>
    db.transaction('rw', db.sesiones, db.banco, db.grabaciones, db.ajustes, async () => {
      await Promise.all([db.sesiones.clear(), db.banco.clear(), db.grabaciones.clear()]);
      await Promise.all([
        db.sesiones.bulkPut(sesiones),
        db.banco.bulkPut(banco),
        db.grabaciones.bulkPut(grabaciones),
      ]);
      if (datos.ajustes) {
        // Los ajustes NO llevan metadatos de sincronización a propósito: son
        // preferencias de este dispositivo (tema, duración preferida), no
        // datos del programa. No tiene sentido sincronizarlas.
        await db.ajustes.put({
          ...(datos.ajustes as Ajustes),
          id: CLAVE_AJUSTES,
          // Quien restaura un respaldo no es alguien que abre la aplicación
          // por primera vez: no se le vuelve a mostrar el onboarding.
          onboardingVisto: true,
        });
      }
    }),
  );

  return {
    sesiones: sesiones.length,
    banco: banco.length,
    grabaciones: grabaciones.length,
  };
}

export const AVISO_SIN_AUDIO =
  'El respaldo JSON no incluye audio, nunca lo incluyó. Las grabaciones vuelven con su ficha —fecha, duración, a qué sesión pertenecen— pero sin el sonido: para eso están las copias que descargaste aparte.';

export const AVISO_REEMPLAZO =
  'Restaurar REEMPLAZA lo que tenés ahora en este dispositivo. Si hay algo que no esté en el respaldo, se pierde. Descargá un respaldo actual antes de continuar.';
